import type { Lesson } from './types.js';

// =============================================================================
// SECTION 4 — Fluent (B2/C1). Themes: Dune, Inception, Death Note, Interstellar.
// =============================================================================

export const U16_LESSONS: Lesson[] = [
  {
    id: 'u16-l1',
    title: 'Passive voice',
    popCulture: 'Dune',
    xpReward: 35,
    exercises: [
      {
        id: 'u16-l1-e1',
        type: 'multiple_choice',
        prompt: '"Arrakis ___ by the Harkonnens."',
        choices: ['was ruled', 'ruled', 'is ruling', 'have ruled'],
        answerIndex: 0,
        newWord: 'was ruled',
      },
      {
        id: 'u16-l1-e2',
        type: 'fill_blank',
        prompt: '"The spice ___ (produce) on Arrakis."',
        answer: 'is produced',
      },
      {
        id: 'u16-l1-e3',
        type: 'translate',
        prompt: 'Translate: "O livro foi escrito por Frank Herbert."',
        from: 'pt',
        to: 'en',
        answer: 'The book was written by Frank Herbert.',
      },
      {
        id: 'u16-l1-e4',
        type: 'reorder',
        prompt: 'Build: "Paul was trained by the Bene Gesserit."',
        tokens: ['Paul', 'was', 'trained', 'by', 'the', 'Bene', 'Gesserit', '.'],
        answer: ['Paul', 'was', 'trained', 'by', 'the', 'Bene', 'Gesserit', '.'],
      },
    ],
  },
  {
    id: 'u16-l2',
    title: 'Complex conditionals',
    popCulture: 'Inception',
    xpReward: 35,
    exercises: [
      {
        id: 'u16-l2-e1',
        type: 'multiple_choice',
        prompt: '"If Cobb had stayed in limbo, he ___ his kids again."',
        choices: ['would not have seen', 'will not see', 'did not see', 'is not seeing'],
        answerIndex: 0,
        newWord: 'third conditional',
      },
      {
        id: 'u16-l2-e2',
        type: 'fill_blank',
        prompt: '"If I ___ (know), I would have told you."',
        answer: 'had known',
      },
      {
        id: 'u16-l2-e3',
        type: 'translate',
        prompt: 'Translate: "Se tivéssemos saído antes, teríamos chegado."',
        from: 'pt',
        to: 'en',
        answer: 'If we had left earlier, we would have arrived.',
      },
    ],
  },
];

export const U17_LESSONS: Lesson[] = [
  {
    id: 'u17-l1',
    title: 'Phrasal verbs',
    popCulture: 'Death Note',
    xpReward: 30,
    exercises: [
      {
        id: 'u17-l1-e1',
        type: 'multiple_choice',
        prompt: 'L said: "We need to ___ up the case." (reabrir / recomeçar)',
        choices: ['take', 'look', 'break', 'open'],
        answerIndex: 3,
        newWord: 'open up',
      },
      {
        id: 'u17-l1-e2',
        type: 'match_pairs',
        prompt: 'Match phrasal verbs.',
        pairs: [
          { left: 'give up', right: 'desistir' },
          { left: 'look after', right: 'cuidar' },
          { left: 'come across', right: 'encontrar' },
          { left: 'turn down', right: 'recusar' },
        ],
      },
      {
        id: 'u17-l1-e3',
        type: 'fill_blank',
        prompt: 'Light decided to ___ on his plan. (continuar)',
        answer: 'carry on',
      },
      {
        id: 'u17-l1-e4',
        type: 'translate',
        prompt: 'Translate: "Eu desisti do projeto."',
        from: 'pt',
        to: 'en',
        answer: 'I gave up on the project.',
        accepts: ['I gave up the project.'],
      },
    ],
  },
];

export const U18_LESSONS: Lesson[] = [
  {
    id: 'u18-l1',
    title: 'Abstract ideas — time',
    popCulture: 'Interstellar',
    xpReward: 40,
    exercises: [
      {
        id: 'u18-l1-e1',
        type: 'multiple_choice',
        prompt: 'Cooper: "Love is ___ transcends dimensions."',
        choices: ['something that', 'who', 'where', 'which is'],
        answerIndex: 0,
        newWord: 'something that',
      },
      {
        id: 'u18-l1-e2',
        type: 'reorder',
        prompt: 'Build: "Time is a flat circle."',
        tokens: ['Time', 'is', 'a', 'flat', 'circle', '.'],
        answer: ['Time', 'is', 'a', 'flat', 'circle', '.'],
      },
      {
        id: 'u18-l1-e3',
        type: 'translate',
        prompt: 'Translate: "Time runs differently here."',
        from: 'en',
        to: 'pt',
        answer: 'O tempo passa diferente aqui.',
        accepts: ['O tempo corre diferente aqui.'],
      },
      {
        id: 'u18-l1-e4',
        type: 'speak',
        prompt: 'Philosophical moment.',
        target: 'Love is the one thing that transcends time and space.',
      },
    ],
  },
];

export const U19_LESSONS: Lesson[] = [
  {
    id: 'u19-l1',
    title: 'Idioms',
    popCulture: 'Rick and Morty',
    xpReward: 35,
    exercises: [
      {
        id: 'u19-l1-e1',
        type: 'multiple_choice',
        prompt: '"It\'s raining cats and dogs." means…',
        choices: ['Está chovendo muito', 'Há animais na rua', 'Faz frio', 'Está ensolarado'],
        answerIndex: 0,
        newWord: 'idiom',
      },
      {
        id: 'u19-l1-e2',
        type: 'match_pairs',
        prompt: 'Match idioms.',
        pairs: [
          { left: 'Break a leg', right: 'Boa sorte' },
          { left: 'Piece of cake', right: 'Muito fácil' },
          { left: 'Hit the sack', right: 'Ir dormir' },
          { left: 'Once in a blue moon', right: 'Raramente' },
        ],
      },
      {
        id: 'u19-l1-e3',
        type: 'translate',
        prompt: 'Translate: "Wubba lubba dub dub!" (use "I am in great pain" as the meaning)',
        from: 'en',
        to: 'pt',
        answer: 'Eu estou com muita dor.',
        accepts: ['Estou com muita dor.'],
      },
    ],
  },
];

export const U20_LESSONS: Lesson[] = [
  {
    id: 'u20-l1',
    title: 'Nuance — hedging',
    popCulture: 'True Detective',
    xpReward: 40,
    exercises: [
      {
        id: 'u20-l1-e1',
        type: 'multiple_choice',
        prompt: 'Softer version: "You are wrong." → "You ___ be wrong."',
        choices: ['might', 'must', 'will', 'have to'],
        answerIndex: 0,
        newWord: 'might',
      },
      {
        id: 'u20-l1-e2',
        type: 'fill_blank',
        prompt: 'Rust: "Time is a flat circle, ___ matter what we do." (Regardless)',
        answer: 'no',
      },
      {
        id: 'u20-l1-e3',
        type: 'translate',
        prompt: 'Translate: "Talvez eu esteja errado."',
        from: 'pt',
        to: 'en',
        answer: 'I might be wrong.',
        accepts: ['Maybe I am wrong.', 'Maybe I\'m wrong.'],
      },
      {
        id: 'u20-l1-e4',
        type: 'speak',
        prompt: 'Express uncertainty.',
        target: 'I may have been mistaken about that.',
      },
    ],
  },
];
